import React, { useState, useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar from '../components/Sidebar';
import Dashboard from '../components/Dashboard';
import Transactions from '../components/Transactions';
import Insights from '../components/Insights';
import Budgets from '../components/Budgets';
import Recurring from '../components/Recurring';
import AIInsights from '../components/AIInsights';
import { Toast } from '../components/UI';
import { useTransactions, useBudgets, useRecurring } from '../hooks';
import { useLang } from '../context/LangContext';
import { useAuth } from '../auth/useAuth';
import LoginPage from '../pages/LoginPage';

function DashboardShell({ session, onLogout, logoutDisabled }) {
  const { t } = useLang();
  const [page, setPage] = useState('dashboard');
  const [demoRole, setDemoRole] = useState(session.role);
  const [toast, setToast] = useState({ msg: '', visible: false });
  const [toastTimer, setTimer] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  const actualRole = session.role;

  const { transactions, addTransaction, updateTransaction, deleteTransaction } = useTransactions();
  const { budgets, addBudget, deleteBudget } = useBudgets();
  const { recurring, addRecurring, toggleRecurring, deleteRecurring } = useRecurring();

  useEffect(() => {
    setDemoRole(session.role);
  }, [session.role]);

  const showToast = useCallback((msg) => {
    if (toastTimer) clearTimeout(toastTimer);
    setToast({ msg, visible: true });
    const nextTimer = setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2500);
    setTimer(nextTimer);
  }, [toastTimer]);

  useEffect(() => {
    return () => {
      if (toastTimer) clearTimeout(toastTimer);
    };
  }, [toastTimer]);

  const handleRoleChange = (nextDemoRole) => {
    setDemoRole(nextDemoRole);
    showToast(nextDemoRole === 'admin' ? `🧪 ${t('toasts.roleAdmin')}` : `🧪 ${t('toasts.roleViewer')}`);
  };

  const exportCSV = () => {
    const headers = ['Date', 'Description', 'Category', 'Type', 'Amount'];
    const rows = transactions.map((tx) => [tx.date, tx.desc, tx.cat, tx.type, tx.amount]);
    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'finflow-transactions.csv';
    anchor.click();
    showToast(`📁 ${t('toasts.csvExport')}`);
  };

  const pageKeyMap = {
    dashboard: 'dashboard',
    transactions: 'transactions',
    insights: 'insights',
    budgets: 'budgets',
    recurring: 'recurring',
    'ai-insights': 'aiInsights',
  };
  const pageKey = pageKeyMap[page] || 'dashboard';
  const meta = {
    title: t(`topbar.pages.${pageKey}.title`),
    sub: t(`topbar.pages.${pageKey}.sub`),
  };

  useEffect(() => {
    setFabOpen(false);
  }, [page]);

  const fabActions = [
    { id: 'tx', label: t('topbar.addTransaction'), onClick: () => setPage('transactions'), angle: -120 },
    { id: 'budget', label: t('budgets.addButton'), onClick: () => setPage('budgets'), angle: -90 },
    { id: 'rec', label: t('recurring.addButton'), onClick: () => setPage('recurring'), angle: -60 },
  ];

  return (
    <div className="app-shell" style={{ display: 'flex', minHeight: '100vh' }}>
      <div
        className={`mobile-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-label="Close sidebar overlay"
      />

      <div className={`sidebar-wrapper ${sidebarOpen ? 'open' : ''}`}>
        <Sidebar
          active={page}
          onNavigate={(id) => setPage(id)}
          role={demoRole}
          actualRole={actualRole}
          onRoleChange={handleRoleChange}
          setSidebar={setSidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={session}
          onLogout={onLogout}
          logoutDisabled={logoutDisabled}
        />
      </div>

      <div className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div
          className="topbar"
          style={{
            background: 'linear-gradient(180deg, rgba(15, 21, 37, 0.86), rgba(15, 21, 37, 0.62))',
            borderBottom: '1px solid rgba(171, 194, 255, 0.18)',
            padding: '16px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            animation: 'fadeUp 0.4s cubic-bezier(.4,0,.2,1) both',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hamburger"
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text)',
                fontSize: 20,
                cursor: 'pointer',
                padding: 4,
              }}
            >
              ☰
            </button>
            <div>
              <div className="topbar-title" style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.4px' }}>{meta.title}</div>
              <div className="topbar-subtitle" style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1 }}>{meta.sub}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              onClick={exportCSV}
              className="export-btn-text"
              style={btnStyle}
              onMouseEnter={(event) => {
                event.currentTarget.style.color = 'var(--text)';
                event.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.color = 'var(--muted)';
                event.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }}
            >
              ↓ {t('topbar.export')}
            </button>
            <button
              onClick={exportCSV}
              className="export-btn-icon"
              style={{
                display: 'none',
                background: 'none',
                border: 'none',
                color: 'var(--text)',
                fontSize: 16,
                cursor: 'pointer',
                padding: 6,
              }}
            >
              ↓
            </button>
            {actualRole === 'admin' && (
              <>
                <button
                  onClick={() => setPage('transactions')}
                  className="add-tx-btn-text"
                  style={{ ...btnStyle, background: 'var(--accent)', color: '#fff', border: 'none' }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.background = '#6fa3ff';
                    event.currentTarget.style.transform = 'translateY(-1px)';
                    event.currentTarget.style.boxShadow = '0 4px 16px rgba(79,143,255,0.4)';
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.background = 'var(--accent)';
                    event.currentTarget.style.transform = '';
                    event.currentTarget.style.boxShadow = '';
                  }}
                >
                  + {t('topbar.addTransaction')}
                </button>
                <button
                  onClick={() => setPage('transactions')}
                  className="add-tx-btn-icon"
                  style={{
                    display: 'none',
                    background: 'var(--accent)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    width: 36,
                    height: 36,
                    fontSize: 18,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.background = '#6fa3ff';
                    event.currentTarget.style.transform = 'translateY(-1px)';
                    event.currentTarget.style.boxShadow = '0 4px 16px rgba(79,143,255,0.4)';
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.background = 'var(--accent)';
                    event.currentTarget.style.transform = '';
                    event.currentTarget.style.boxShadow = '';
                  }}
                >
                  +
                </button>
              </>
            )}
          </div>
        </div>

        <div className="content-area" style={{ flex: 1, overflowY: 'auto', padding: 28, scrollBehavior: 'smooth', background: 'transparent' }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={page}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {page === 'dashboard' && <Dashboard transactions={transactions} role={actualRole} />}
              {page === 'transactions' && (
                <Transactions
                  transactions={transactions}
                  role={actualRole}
                  onAdd={addTransaction}
                  onUpdate={updateTransaction}
                  onDelete={deleteTransaction}
                  onToast={showToast}
                />
              )}
              {page === 'insights' && <Insights transactions={transactions} />}
              {page === 'budgets' && (
                <Budgets
                  budgets={budgets}
                  transactions={transactions}
                  role={actualRole}
                  onAdd={addBudget}
                  onDelete={deleteBudget}
                  onToast={showToast}
                />
              )}
              {page === 'recurring' && (
                <Recurring
                  recurring={recurring}
                  role={actualRole}
                  onAdd={addRecurring}
                  onToggle={toggleRecurring}
                  onDelete={deleteRecurring}
                  onToast={showToast}
                />
              )}
              {page === 'ai-insights' && <AIInsights transactions={transactions} budgets={budgets} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {actualRole === 'admin' && (
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 120 }}>
          <AnimatePresence>
            {fabOpen && (
              <motion.div
                initial="closed"
                animate="open"
                exit="closed"
                variants={{
                  open: { transition: { staggerChildren: 0.08, delayChildren: 0.02 } },
                  closed: { transition: { staggerChildren: 0.05, staggerDirection: -1 } },
                }}
                style={{ position: 'absolute', right: 0, bottom: 0, width: 220, height: 220, pointerEvents: 'none' }}
              >
                {fabActions.map((action) => {
                  const radius = 92;
                  const x = Math.cos((action.angle * Math.PI) / 180) * radius;
                  const y = Math.sin((action.angle * Math.PI) / 180) * radius;
                  return (
                    <motion.button
                      key={action.id}
                      variants={{
                        open: { opacity: 1, scale: 1, x, y },
                        closed: { opacity: 0, scale: 0.6, x: 0, y: 0 },
                      }}
                      transition={{ type: 'spring', stiffness: 320, damping: 24 }}
                      onClick={() => {
                        setFabOpen(false);
                        action.onClick();
                      }}
                      style={{
                        position: 'absolute',
                        right: 10,
                        bottom: 10,
                        pointerEvents: 'auto',
                        border: '1px solid rgba(171, 194, 255, 0.24)',
                        background: 'linear-gradient(180deg, rgba(22,29,48,0.9), rgba(15,21,37,0.92))',
                        color: 'var(--text)',
                        borderRadius: 999,
                        padding: '8px 12px',
                        fontSize: 11,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 14px 28px rgba(3, 6, 15, 0.45)',
                      }}
                    >
                      {action.label}
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            onClick={() => setFabOpen((value) => !value)}
            animate={{ rotate: fabOpen ? 45 : 0, scale: fabOpen ? 1.08 : 1 }}
            transition={{ type: 'spring', stiffness: 360, damping: 22 }}
            style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              border: 'none',
              background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
              color: '#fff',
              fontSize: 30,
              lineHeight: 1,
              cursor: 'pointer',
              boxShadow: '0 18px 40px rgba(35, 78, 180, 0.45)',
            }}
            aria-label="Quick actions"
          >
            +
          </motion.button>
        </div>
      )}

      <Toast message={toast.msg} visible={toast.visible} />
    </div>
  );
}

export default function App() {
  const { session, login, logout } = useAuth();
  const [showDashboard, setShowDashboard] = useState(Boolean(session));
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    if (session) {
      setShowDashboard(true);
      return;
    }

    setShowDashboard(false);
    setIsLoggingOut(false);
  }, [session]);

  const handleLogin = useCallback(async ({ email, password }) => {
    const result = await login(email, password);
    if (result?.ok) {
      setShowDashboard(true);
    }
    return result;
  }, [login]);

  const handleLogout = useCallback(() => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    setShowDashboard(false);

    setTimeout(() => {
      logout();
    }, 320);
  }, [isLoggingOut, logout]);

  return (
    <AnimatePresence mode="wait" initial={false}>
      {session && showDashboard ? (
        <motion.div
          key={`dashboard-${session.email}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.995, filter: 'blur(1.5px)' }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          <DashboardShell
            session={session}
            onLogout={handleLogout}
            logoutDisabled={isLoggingOut}
          />
        </motion.div>
      ) : (
        <motion.div
          key="login"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24 }}
        >
          <LoginPage onLogin={handleLogin} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8,
  fontSize: 12, fontFamily: 'var(--font-sans)', fontWeight: 500,
  cursor: 'pointer', transition: 'all 0.18s cubic-bezier(.4,0,.2,1)',
  background: 'rgba(255,255,255,0.05)', color: 'var(--muted)',
  border: '1px solid var(--border2)',
};
